import "../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../assets/fa-icons/css/sharp-regular.css";
import "../../../../assets/fa-icons/css/sharp-solid.css";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import React, { useEffect, useState } from "react";
import { TabId } from "../types/tab-id.ts";
import { getPropertyDetailsTabsMeta } from "../utils/property-details-tab-definitions.ts";
import { FontStyles } from "../../../utils/font-styles.ts";
import { TabsList, TabsTrigger } from "../../../components/base/tabs.tsx";
import { cn } from "../../../utils/cn.ts";
import { CachingTabsView, type TabDefinition } from "../components/caching-tabs-view.tsx";
import { AddressTextDisplay } from "../../address-text-display.tsx";
import { AgentContactBar } from "./components/agent-contact-bar.tsx";
import { GalleryActionButton } from "./components/gallery-action-button.tsx";
import { Sharing, type ShareMethod } from "../../../components/sharing.tsx";
import { Button } from "../../../components/buttons/button.tsx";
import { CloseButton } from "../../../components/buttons/close-button.tsx";
import { GalleryModalUnderlineTabsTrigger } from "./components/gallery-modal-underline-tabs-trigger.tsx";
import { PropertyMap } from "../components/property-map.tsx";
import { PropertyStreetView } from "../components/property-street-view.tsx";
import { Photos } from "./components/photos.tsx";
import { VirtualTour } from "../components/virtual-tour.tsx";
import { FloorPlanProxy } from "../components/floor-plan-proxy.tsx";
import { PropertyVideo } from "../components/property-video.tsx";
import { AgentContactBarDesktop } from "./components/agent-contact-bar-desktop.tsx";
import { focusStyles } from "../../../utils/focus-styles.ts";
import { usePropertyDetailsContext } from "../../contexts/property-details-context.tsx";
import { getPriceDisplay } from "../../utils/price-helper.ts";
import { TrackEvent } from "../../../utils/analytics.ts";
import { useWatchlist } from "../../utils/use-watchlist.ts";

export interface GalleryModalProps {
  heroActiveTabId: TabId;
  isOpen: boolean;
}

const trackShare = (method: ShareMethod) => {
  TrackEvent({ name: "share", value: 10, content_type: "property_details", method });
};

export const GalleryModal = ({ heroActiveTabId, isOpen }: GalleryModalProps) => {
  const {
    propertyId,
    displayAddress,
    price,
    rental,
    offerType,
    images = [],
    virtualTourUrl,
    isVirtualTourExternalRedirect = false,
    isVideoExternalRedirect = false,
    videoUrl,
    floorPlanResourceUrl,
    lat,
    lon,
    propertyReference,
    propertySource,
    shareUrl,
  } = usePropertyDetailsContext();
  const { loading, isWatchlisted, toggle } = useWatchlist(propertyId, propertySource, "property_details");

  const hasLocation = lat != null && lon != null;

  const priceDisplay = getPriceDisplay(offerType, price, rental);

  const [activeTabId, setActiveTabId] = useState(heroActiveTabId);
  const [isShareExpanded, setIsShareExpanded] = useState(false);
  const watchlistAriaLabel = isWatchlisted ? "Remove property from watchlist" : "Add property to watchlist";
  const sharePropertyAriaLabel = "Share property";
  const scrollableContent = (content: React.ReactNode) => (
    <div className="p-3.5 md:p-6 lg:p-6.5 overflow-y-auto flex-1">{content}</div>
  );

  const tabsMeta = getPropertyDetailsTabsMeta(images);
  const TABS: TabDefinition[] = [
    {
      ...tabsMeta.photos,
      includeTab: images.length > 0,
      content: scrollableContent(<Photos />),
    },
    {
      ...tabsMeta.virtualTour,
      includeTab: !!virtualTourUrl,
      content: <VirtualTour url={virtualTourUrl} isVirtualTourExternalRedirect={isVirtualTourExternalRedirect} />,
    },
    {
      ...tabsMeta.video,
      includeTab: !!videoUrl,
      skipClickReporting: isVideoExternalRedirect,
      content: !isVideoExternalRedirect ? (
        scrollableContent(
          <PropertyVideo url={videoUrl} isConstrainedView={true} isVideoExternalRedirect={isVideoExternalRedirect} />,
        )
      ) : (
        <PropertyVideo url={videoUrl} isVideoExternalRedirect={isVideoExternalRedirect} />
      ),
    },
    {
      ...tabsMeta.floorPlan,
      includeTab: !!floorPlanResourceUrl,
      content: floorPlanResourceUrl ? (
        <FloorPlanProxy floorPlanResourceUrl={floorPlanResourceUrl} isModalView={true} />
      ) : null,
    },
    {
      ...tabsMeta.map,
      includeTab: hasLocation,
      content: hasLocation ? (
        <PropertyMap lat={lat} lon={lon} displayAddress={displayAddress ?? ""} isTabActive={activeTabId === "map"} />
      ) : null,
    },
    {
      ...tabsMeta.streetView,
      includeTab: hasLocation,
      content: hasLocation ? (
        <PropertyStreetView lat={lat} lon={lon} isTabActive={activeTabId === "street-view"} />
      ) : null,
    },
  ];

  useEffect(() => {
    if (isOpen) {
      setActiveTabId(heroActiveTabId);
    }
  }, [isOpen, heroActiveTabId]);

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay />
      <DialogPrimitive.Content
        className={cn(
          "twp",
          "flex flex-col h-full bg-cloud fixed top-0 left-0 right-0 bottom-0 z-[9999]",
          "focus:outline-none",
          "data-[state=open]:animate-fade-in-grow data-[state=closed]:animate-fade-out-shrink",
        )}
      >
        {/* Visually hidden title required by Radix for an accessible dialog name */}
        <DialogPrimitive.Title className="sr-only">Property gallery</DialogPrimitive.Title>

        <div className="bg-north-sea flex items-center justify-between gap-3.5 px-3.5 py-5 md:px-6 md:py-4.5 lg:px-6.5 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.4)] shrink-0">
          <div className="flex flex-col gap-2 md:gap-2.5 max-w-[calc(100%-40px)]">
            <AddressTextDisplay className={cn(FontStyles["body-xl"], "text-neutral-white font-medium !leading-none")} />
            <p className={`text-neutral-white ${FontStyles["body-md"]} !leading-none`}>{priceDisplay}</p>
          </div>
          <DialogPrimitive.Close asChild>
            <CloseButton aria-label="Close gallery" variant="onDark" size="lg" className="size-7.5 p-0" />
          </DialogPrimitive.Close>
        </div>

        <CachingTabsView
          tabs={TABS}
          activeTabId={activeTabId}
          onTabChange={setActiveTabId}
          reportingPropertyReference={propertyReference}
          reportingPropertySource={propertySource}
          tabsListPosition={"top"}
          className="flex-1 flex flex-col overflow-hidden"
          tabsList={
            <div className="bg-neutral-white border-b border-foggy-firth flex items-center justify-between px-3.5 md:px-6 lg:px-6.5 shrink-0">
              <TabsList className="flex-1 md:flex-none gap-2.5 items-center py-2.5 md:py-0" aria-label="Gallery tabs">
                {TABS.filter((tab) => tab.includeTab).map((tab) => {
                  const isActive = activeTabId === tab.id;

                  return (
                    <span key={tab.id} className="contents">
                      {/* Mobile: pill button */}
                      <TabsTrigger value={tab.id} asChild>
                        <Button
                          colorVariant={isActive ? "secondary" : "accent"}
                          variant={isActive ? "solid" : "outlined"}
                          renderLeadingIcon={() => (
                            <i
                              className={cn(
                                `fa-${tab.icon} fa-sharp fa-regular`,
                                isActive ? "text-neutral-white" : "text-loch",
                                FontStyles["body-l"],
                              )}
                              aria-hidden="true"
                            />
                          )}
                          label={tab.label}
                          labelStyle="sr-only"
                          className={cn("md:hidden flex-1 px-1 py-1 h-9", isActive ? "border-2 border-loch" : "")}
                        />
                      </TabsTrigger>
                      {/* Tablet/Desktop: underline tab */}
                      <TabsTrigger value={tab.id} asChild>
                        <GalleryModalUnderlineTabsTrigger
                          icon={tab.icon}
                          label={tab.label}
                          className="hidden md:flex"
                          isActive={isActive}
                        />
                      </TabsTrigger>
                    </span>
                  );
                })}
              </TabsList>

              {/* Desktop-only row: 12px between the social buttons, per the tablet-and-up spacing. */}
              <div className={cn("items-center hidden lg:flex", isShareExpanded ? "gap-3" : "gap-3.5")}>
                <Sharing
                  url={shareUrl}
                  nativeShareAriaLabel={sharePropertyAriaLabel}
                  expandAriaLabel={sharePropertyAriaLabel}
                  nativeShareLabel={<span className="font-medium text-slate">Share</span>}
                  onShare={trackShare}
                  expandable
                  isExpanded={isShareExpanded}
                  onExpandedChange={setIsShareExpanded}
                  className={cn(
                    FontStyles["body-md"],
                    "shadow-loch-800",
                    focusStyles,
                    "lg:active:!bg-loch-100",
                    "lg:text-body-l flex gap-1.5 md:gap-2 lg:py-4.5 lg:px-1.5 lg:hover:bg-loch-50 items-center cursor-pointer !leading-none",
                  )}
                  iconClassName="text-loch"
                />
                <GalleryActionButton
                  icon="heart"
                  ariaLabel={watchlistAriaLabel}
                  ariaPressed={isWatchlisted}
                  label={isWatchlisted ? "Saved" : "Save"}
                  isSolidIcon={isWatchlisted}
                  loading={loading}
                  onClick={() => void toggle()}
                />
              </div>
            </div>
          }
          contentClassName="flex-1 flex flex-col min-h-0 lg:flex-row"
        >
          {/* Desktop-only sidebar */}
          <AgentContactBarDesktop />
        </CachingTabsView>

        {/* Mobile/tablet agent bar */}
        <AgentContactBar
          onShare={trackShare}
          onWatchClick={() => void toggle()}
          watchlistLoading={loading}
          isWatchlisted={isWatchlisted}
          watchlistAriaLabel={watchlistAriaLabel}
          sharePropertyAriaLabel={sharePropertyAriaLabel}
        />
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
};
